<script lang="ts">
  /**
   * Bot Tour bootstrap route.
   *
   * Lê o token de #t=..., troca por sessão demo de 90s no backend e
   * delega o tour a <BotTourController /> (montado globalmente em App.svelte),
   * que sobrevive às mudanças de rota.
   */
  import { onMount } from "svelte";
  import { api, setAccessToken } from "$lib/api/client";
  import { auth } from "$lib/stores/auth.svelte";
  import { navigate } from "$lib/router.svelte";

  let phase = $state<"loading" | "error">("loading");
  let error = $state<string | null>(null);

  function readToken(): string | null {
    const hash = window.location.hash || "";
    const m = hash.match(/[#&]t=([A-Za-z0-9_\-]+)/);
    return m ? m[1] : null;
  }

  async function bootstrap() {
    const token = readToken();
    if (!token) {
      phase = "error";
      error = "Token ausente. Use o link enviado pelo Telegram.";
      return;
    }
    try {
      const r = await api<{
        access_token: string;
        expires_in: number;
        tour_plan: string[];
      }>("/bot-tour/start", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      setAccessToken(r.access_token);
      sessionStorage.setItem("takite.demo", "1");
      sessionStorage.setItem("takite.bot_tour", "1");
      sessionStorage.setItem(
        "takite.bot_tour.plan",
        JSON.stringify(r.tour_plan || ["/dashboard"]),
      );
      sessionStorage.setItem(
        "takite.bot_tour.deadline",
        String(Date.now() + (r.expires_in || 90) * 1000),
      );
      localStorage.setItem("takite.onboarded", "1");
      await auth.bootstrap();

      // Limpa o token do histórico do navegador.
      try { history.replaceState(null, "", "/bot-tour"); } catch {}

      // Dispara o controller global e vai para a primeira tela.
      window.dispatchEvent(new CustomEvent("takite:bot-tour-start"));
      navigate("/dashboard", { replace: true });
    } catch (e: any) {
      phase = "error";
      error = e?.body?.detail || e?.message || "Falha ao iniciar tour.";
    }
  }

  onMount(bootstrap);
</script>

{#if phase === "loading"}
  <div class="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-900 to-emerald-950">
    <div class="text-center space-y-4 max-w-sm">
      <div class="text-5xl animate-pulse">🤖</div>
      <h1 class="text-xl font-semibold text-white">Modo demonstração automatizada</h1>
      <p class="text-sm text-slate-300">
        Você está prestes a ver o <strong>TáKite</strong> funcionando sozinho, com dados fictícios.
      </p>
      <p class="text-xs text-slate-400">Validando autorização do robô…</p>
    </div>
  </div>
{:else}
  <div class="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-900 to-rose-950">
    <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl text-center space-y-4 max-w-sm p-6">
      <div class="text-5xl">⛔</div>
      <h1 class="text-lg font-semibold text-slate-900 dark:text-white">Acesso não autorizado</h1>
      <p class="text-sm text-slate-600 dark:text-slate-300">{error}</p>
      <div class="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-left">
        💡 Para gerar um novo link, envie <code class="font-mono">/ajuda</code> no Telegram
        do TáKite e clique em <strong>“Autorizar robô (90s)”</strong>. Cada link é único e expira rápido.
      </div>
      <div class="grid grid-cols-2 gap-2">
        <button class="btn-primary" onclick={() => navigate("/landing")}>Ir para o site</button>
        <a
          class="rounded-xl border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium py-2 px-3 text-sm transition inline-flex items-center justify-center"
          href="https://t.me/marciopessoal_bot"
          target="_blank"
          rel="noopener noreferrer"
        >
          Abrir Telegram
        </a>
      </div>
    </div>
  </div>
{/if}
